package de.oevbs.kunde.kundenportal2.services.contact

import de.oevbs.kosmos.ui.elements.button.btn
import de.oevbs.kosmos.ui.forms.checkboxgroup.checkboxGroup
import de.oevbs.kosmos.ui.forms.inputgroup.inputText
import de.oevbs.kosmos.ui.forms.selectmenu.selectMenu
import de.oevbs.kosmos.ui.forms.textarea.standard.TextAreaSize
import de.oevbs.kosmos.ui.forms.textarea.textArea
import de.oevbs.kosmos.ui.icons.CustomIcons
import de.oevbs.kosmos.ui.icons.Icon
import de.oevbs.kosmos.ui.icons.MaterialIconsOutlined
import de.oevbs.kosmos.ui.icons.icon
import de.oevbs.kosmos.ui.layouts.card
import de.oevbs.kunde.kundenportal2.GeneralProgressStep
import de.oevbs.kunde.kundenportal2.components.*
import de.oevbs.kunde.kundenportal2.model.services.contact.*
import de.oevbs.kunde.kundenportal2.model.services.contact.types.ComplaintReason
import de.oevbs.kunde.kundenportal2.model.services.contact.types.ComplaintSubject
import de.oevbs.kunde.kundenportal2.model.services.contact.types.ContactMessageType
import de.oevbs.kunde.kundenportal2.model.services.contact.types.SolutionProposal
import de.oevbs.kunde.kundenportal2.navigation.Page
import dev.fritz2.core.RenderContext
import dev.fritz2.headless.validation.Severity
import kotlinx.browser.window
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.mapNotNull

fun RenderContext.contactMessagePage(page: Page) {
    val contactMessageStore = ContactMessageStore(job)
    contactMessageStore.initModel(Unit)

    headerImageLayout {
        title(page.linkTitle)
        content {
            contentContainer {
                combine(
                    globalInitTracker.data,
                    contactMessageStore.initFailedFlow,
                    contactMessageStore.progress,
                    ::Triple
                ).render { (loading, failed, progressStep) ->
                    if (loading) return@render
                    if (failed) {
                        noPartnerFoundMessage()
                        return@render
                    }
                    when (progressStep) {
                        GeneralProgressStep.SUBMIT1 -> contactMessageForm(contactMessageStore)
                        GeneralProgressStep.SUCCESS2 -> successPage(contactMessageStore.lastMessageSent)
                    }
                }
            }
        }
    }
}

private fun RenderContext.noPartnerFoundMessage() {
    noPartnerDataFoundMessage {
        btn {
            label("Allgemeine Mitteilung schreiben")
        }.clicks handledBy {
            window.open(
                "https://www.oeffentliche.de/content/service/kontakt/ihre-mitteilung-an-uns/",
                target = "_blank"
            )
        }
    }
}

private fun RenderContext.contactMessageForm(contactMessageStore: ContactMessageStore) {
    paragraph {
        +"Sie möchten uns etwas mitteilen? Nutzen Sie unser Formular."
    }

    div {
        sectionHeader("Mitteilungsart wählen")
        card {
            imageToggleKupo<String> {
                value.single(contactMessageStore.map(ContactMessage.type()))
                options = ContactMessageType.entries.map(ContactMessageType::text)
                optionsIcon = { type ->
                    when (type) {
                        ContactMessageType.CONSULTANT_MESSAGE.text -> CustomIcons.person_protected
                        ContactMessageType.CONTRACT_INFORMATION.text -> CustomIcons.regulations
                        ContactMessageType.LOSS_INFORMATION.text -> CustomIcons.vase_crack
                        ContactMessageType.COMPLAINT_MESSAGE.text -> MaterialIconsOutlined.feedback
                        else -> MaterialIconsOutlined.chat
                    }
                }
            }
        }
    }

    val typeStore = contactMessageStore.map(ContactMessage.type())
    val claimRefsStore = contactMessageStore.initData.map(ContactMessageInit.claimRefs())

    combine(typeStore.data, claimRefsStore.data, ::Pair).render typeDependantCards@{ (type, claimRefs) ->
        when (type) {
            ContactMessageType.CONTRACT_INFORMATION.text -> {
                renderContractInformationCard(contactMessageStore)
            }

            ContactMessageType.LOSS_INFORMATION.text -> {
                if (claimRefs.isNotEmpty()) renderLossInformationCard(contactMessageStore, claimRefs)
                else {
                    renderNoClaimsMessage()
                    return@typeDependantCards
                }
            }

            ContactMessageType.CONSULTANT_MESSAGE.text -> {
                renderConsultantMessageCard(contactMessageStore)
            }

            ContactMessageType.COMPLAINT_MESSAGE.text -> {
                renderComplaintMessageCard(contactMessageStore, claimRefs)
            }
        }

        div {
            sectionHeader("Nachricht verfassen")
            card {
                div("flex flex-col gap-4") {
                    if (type == ContactMessageType.CONSULTANT_MESSAGE.text) {
                        div {
                            paragraph {
                                +"Schreiben Sie eine Nachricht an Ihre Beraterin oder Ihren Berater."
                            }
                            inputText {
                                label("Betreff")
                                value(contactMessageStore.map(ContactMessage.subject()))
                            }
                        }
                    } else if (type == ContactMessageType.COMPLAINT_MESSAGE.text) {
                        selectMenu {
                            label("Lösungsvorschlag")
                            options = SolutionProposal.entries.map(SolutionProposal::text)
                            value(contactMessageStore.map(ContactMessage.complaintSolution()))
                        }
                    }

                    textArea {
                        label("Nachricht")
                        size = TextAreaSize.Paragraph
                        value(contactMessageStore.map(ContactMessage.content()))
                    }

                    buttonContainer {
                        btn {
                            label("Nachricht senden")
                            busy(globalSubmitTracker.data)
                        }.clicks handledBy contactMessageStore.submitMessage
                    }
                }
            }
        }
    }
}

private fun RenderContext.renderNoClaimsMessage() {
    card {
        paragraph {
            +"Wie es aussieht, sind Sie gerade schadenfrei oder Ihr Schaden ist noch nicht im System erfasst."
            br {}
            +"Wenn Sie einen neuen Schaden melden möchten, "
            inlineLink(
                "nutzen Sie bitte die Online-Schadenmeldung",
                "https://sfa.oeffentliche.de/static/schaden-online2/index.html#page=main"
            )
            +"."
        }
    }
}

private fun RenderContext.renderContractInformationCard(contactMessageStore: ContactMessageStore) {
    div {
        card {
            +"Wenn Sie einen neuen Schaden zu Ihrem Vertrag melden möchten, "
            inlineLink(
                "nutzen Sie bitte die Online-Schadenmeldung",
                "https://sfa.oeffentliche.de/static/schaden-online2/index.html#page=main"
            )
            +". Für Nachrichten zu bereits gemeldeten Schäden wählen Sie bitte oben die 'Mitteilung zum Schaden' aus."
        }
    }
    div {
        sectionHeader("Vertrag wählen")
        card {
            contactMessageStore.initData.data.render { initData ->
                selectMenu {
                    label("Vertrag")
                    options = initData.contractRefs
                    value(contactMessageStore.map(ContactMessage.contract()))
                }
            }
        }
    }
    renderContractOrClaimDisclaimer(contactMessageStore)
}

private fun RenderContext.renderLossInformationCard(
    contactMessageStore: ContactMessageStore,
    claimRefs: List<MessageReference>
) {
    div {
        card {
            +"Hier können Sie uns Nachrichten zu bereits gemeldeten Schäden senden."
            br {}
            +"Wenn Sie einen neuen Schaden melden möchten, "
            inlineLink(
                "nutzen Sie bitte die Online-Schadenmeldung",
                "https://sfa.oeffentliche.de/static/schaden-online2/index.html#page=main"
            )
            +"."
        }
    }
    div {
        sectionHeader("Schaden wählen")
        card {
            div("flex flex-col gap-2") {
                selectMenu {
                    label("Schaden")
                    options = claimRefs
                    value(contactMessageStore.map(ContactMessage.claim()))
                }
            }
        }
    }
    renderContractOrClaimDisclaimer(contactMessageStore)
}

private fun RenderContext.renderConsultantMessageCard(contactMessageStore: ContactMessageStore) {
    div {
        sectionHeader("Ihr:e Berater:in")
        card {
            div("flex flex-col gap-4") {
                paragraph {
                    +"Ihre Geschäftsstelle ist für Sie da!"
                }
                div("grid grid-cols-1 xl:grid-cols-[max-content_max-content] gap-4") {
                    contactMessageStore.initData.data.mapNotNull { it.agent }.render { agent ->
                        div("flex flex-col gap-2") {
                            h2("vui-label-1") {
                                +agent.agencyName
                            }
                            iconWithText(MaterialIconsOutlined.person, agent.advisor)
                            iconWithText(
                                MaterialIconsOutlined.house,
                                "${agent.address.street} ${agent.address.houseNumber}"
                            )
                            iconWithText(
                                MaterialIconsOutlined.location_on,
                                "${agent.address.postalCode} ${agent.address.city}"
                            )
                            iconWithContent(MaterialIconsOutlined.mail) {
                                inlineLink(text = agent.email, link = "mailto:${agent.email}")
                            }
                            iconWithContent(MaterialIconsOutlined.call) {
                                inlineLink(text = agent.phone, link = "tel:${agent.phone}")
                            }
                            if (agent.fax.isNotEmpty()) {
                                iconWithText(MaterialIconsOutlined.fax, agent.fax)
                            }
                        }
                        div("flex flex-col gap-2") {
                            h2("vui-label-1") {
                                +"Öffnungszeiten"
                            }
                            agent.openings.forEach {
                                p {
                                    +it
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

private fun RenderContext.renderComplaintMessageCard(
    contactMessageStore: ContactMessageStore,
    claimRefs: List<MessageReference>
) {
    div {
        sectionHeader("Anregung oder Beschwerde")
        card {
            div("flex flex-col gap-4") {
                paragraph {
                    +"Sie sind unzufrieden mit uns? Helfen Sie uns, unseren Service zu verbessern, und teilen Sie uns Ihr Anliegen mit."
                }
                div("grid grid-cols-1 xl:grid-cols-[max-content_max-content] gap-4") {
                    div("flex flex-col gap-2") {
                        h2("vui-label-1") {
                            +"Gegenstand der Beschwerde"
                        }
                        checkboxGroup {
                            options = ComplaintSubject.entries.map(ComplaintSubject::text)
                            value(contactMessageStore.map(ContactMessage.complaintSubjects()))
                        }
                    }
                    div("flex flex-col gap-2") {
                        h2("vui-label-1") {
                            +"Grund der Beschwerde"
                        }
                        checkboxGroup {
                            options = ComplaintReason.entries.map(ComplaintReason::text)
                            value(contactMessageStore.map(ContactMessage.complaintReasons()))
                        }
                    }
                }
                contactMessageStore.initData.data.mapNotNull { it.contractRefs }.render { contractRefs ->
                    selectMenu {
                        label("Vertrag")
                        options = contractRefs
                        value(contactMessageStore.map(ContactMessage.contract()))
                    }
                }
                selectMenu {
                    label("Schaden")
                    options = claimRefs
                    disabled(claimRefs.isEmpty())
                    value(contactMessageStore.map(ContactMessage.claim()))
                }
            }
        }
    }
}

private fun RenderContext.renderContractOrClaimDisclaimer(contactMessageStore: ContactMessageStore) {
    div {
        sectionHeader("Hinweise und Informationen")
        card {
            div("flex flex-col gap-4") {
                ul("list-disc list-inside") {
                    li {
                        +"Sie erhalten eine automatische Empfangsbestätigung."
                    }
                    li {
                        +"""Ihre Angaben werden an eine:n für Sie zuständige:n Berater:in weitergegeben. Eine sonstige
                        | Weitergabe an Dritte erfolgt nicht.""".trimMargin()
                    }
                    li {
                        +"Bitte lesen Sie die folgenden "
                        inlineLink(
                            text = "Hinweise zum Datenschutz",
                            link = "https://www.oeffentliche.de/content/oeffentliche/gesetzliche-regelungen/datenschutz/",
                            target = "_blank"
                        )
                        +" und bestätigen Sie diese unten."
                    }
                }
                checkboxSingle {
                    label(
                        """Ja, ich bestätige die Vollständigkeit und Richtigkeit meiner Angaben und erkläre, dass
                            | diese Daten genutzt und elektronisch gespeichert werden dürfen. Die Einwilligung kann 
                            | jederzeit widerrufen werden.""".trimMargin()
                    )
                    value(contactMessageStore.map(ContactMessage.confirmation()))
                }
            }
        }
    }
}

private fun RenderContext.iconWithContent(icon: Icon, content: RenderContext.() -> Unit) {
    div("flex flex-row items-center gap-2") {
        icon("w-4 h-4", icon = icon)
        content()
    }
}

private fun RenderContext.iconWithText(icon: Icon, text: String) =
    iconWithContent(icon) {
        span {
            +text
        }
    }

private fun RenderContext.successPage(lastMessageSent: String) {
    messagePagePre(
        header = "Mitteilung versendet",
        severity = Severity.Success,
        preContent = lastMessageSent
    )
}